import React, { useState, useRef } from "react";

interface CompanyMultiSelectProps {
  companies: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

export const CompanyMultiSelect: React.FC<CompanyMultiSelectProps> = ({ companies, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Filter companies by search
  let filteredCompanies = companies.filter(c => c.toLowerCase().includes(search.toLowerCase()));
  // If no search, show selected companies at the top
  if (!search) {
    const selectedSet = new Set(selected);
    const selectedCompaniesTop = companies.filter(c => selectedSet.has(c));
    const unselectedCompanies = companies.filter(c => !selectedSet.has(c));
    filteredCompanies = [...selectedCompaniesTop, ...unselectedCompanies];
  }

  return (
    <div ref={ref} className="relative inline-block min-w-[220px]">
      <div
        className="flex items-center border rounded px-2 py-1 bg-white cursor-pointer min-h-[36px]"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex-1 text-sm text-[#22223b]">
          {selected.length > 0 ? selected.join(", ") : "Select companies..."}
        </span>
        <span className="ml-2 text-[#0071bd]">&#9662;</span>
      </div>
      {open && (
        <div className="absolute z-10 left-0 right-0 bg-white border rounded shadow-lg mt-1 max-h-60 overflow-auto">
          <div className="px-3 py-2 text-xs text-[#6c757d] font-semibold">Available Companies</div>
          <div className="px-3 pb-2">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Type to search..."
              className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#0071bd]"
            />
          </div>
          {filteredCompanies.length === 0 && (
            <div className="px-3 py-2 text-sm text-[#6c757d]">No companies found</div>
          )}
          {filteredCompanies.map((company, idx) => (
            <div
              key={company + idx}
              className={`px-3 py-2 cursor-pointer text-sm hover:bg-[#f7f9fc] ${selected.includes(company) ? "bg-[#e9ecef]" : ""}`}
              onClick={() => {
                if (selected.includes(company)) {
                  onChange(selected.filter((c) => c !== company));
                } else {
                  onChange([...selected, company]);
                }
              }}
            >
              <input
                type="checkbox"
                checked={selected.includes(company)}
                readOnly
                className="mr-2 align-middle"
              />
              {company}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
